import { TABLE_MEASUREMENTS } from "../types/types";

export const getImageUrl = (name) => {
  return new URL(`../assets/images/${name}.jpg`, import.meta.url).href;
};

export function capitalize(string) {
  if (!string) return string;
  if (string.length === 1) return string.toUpperCase();
  return string[0].toUpperCase() + string.substring(1);
}
export const parsePhoneNumber = (value) => {
  let newVal = value.trim();

  //Remove all non-digits
  newVal = newVal.replace(/\D/g, "");
  return `+${newVal}`;
};

export function getErrorMessage(error, customizedDefault) {
  const defaultMessage =
    customizedDefault ||
    "Sorry, an unexpected error has occurred. Please try again or contact our team if the issue still persists.";

  if (error?.code === "ERR_NETWORK") {
    return "Sorry, we are unable to complete the action due to a network issue. Please check your connection or try again later.";
  }

  const d = error?.response?.data;
  if (d == null) return defaultMessage;
  if (typeof d === "string") return d;

  const single =
    d.errorDetails ||
    d.detail ||
    d.message ||
    d.errorMessage ||
    d.title;
  if (typeof single === "string" && single.trim()) return single;

  const titleDetail = [d.title, d.detail]
    .filter((x) => typeof x === "string" && x.trim())
    .join(": ");
  if (titleDetail) return titleDetail;

  return defaultMessage;
}

export function getMimeType(format) {
  const images = ["png", "jpeg", "webp"];
  const text = ["csv"];

  if (images.includes(format)) {
    return `image/${format}`;
  }
  if (text.includes(format)) {
    return `text/${format}`;
  }
  return "";
}

export function convertCamelToTitle(text) {
  if (!text) return "";
  let baseString = text
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, function (str) {
      return str.toUpperCase();
    });
  return baseString;
}

/* Chart  helper functions */
export function getChartLabel(val) {
  if (!val) return;
  let baseString = val.split("SizingChart")[0];
  baseString = convertCamelToTitle(baseString);

  return baseString;
}

/** Splits template id label (e.g. "Male Top") into display parts for forms. */
export function getChartTemplateMeta(id) {
  const fullLabel = getChartLabel(id) || "—";
  const parts = fullLabel.trim().split(/\s+/);
  return {
    fullLabel,
    gender: parts[0] || "—",
    clothingType: parts.length > 1 ? parts.slice(1).join(" ") : "—",
  };
}

/** Comma-separated size names for list views. */
export function formatSizeLabels(sizes) {
  if (!sizes?.length) return "—";
  return sizes.map((s) => s.name).join(", ");
}

export function getMeasurementsList(sizes, keepNulls) {
  if (!sizes || sizes.length < 1) return [];
  const sampleItem = sizes[0];
  if (keepNulls)
    return Object.keys(sampleItem).filter((m) => m !== "name" && m !== "id");
  return Object.keys(sampleItem).filter((m) => {
    if (m === "name" || m === "id") return false;
    return sizes.some((size) => hasMeasurementValue(size?.[m]));
  });
}

/** Human-readable measurement keys for a chart row. */
export function formatPrimaryMeasurements(sizes) {
  const keys = getMeasurementsList(sizes);
  if (!keys.length) return "—";
  return keys.map((k) => capitalize(k)).join(", ");
}
export function formatChartData(data, options = {}) {
  if (!data || data.sizes?.length < 1) return [];
  const { includeEmptyMeasurements = false } = options;
  const sizes = data.sizes;
  const measurements = includeEmptyMeasurements
    ? getEnabledMeasurementsList(sizes)
    : getMeasurementsList(sizes);

  let newData = {};
  measurements.forEach((measurement) => {
    newData[measurement] = [];
  });
  let finalData = [];
  sizes.forEach((size) => {
    measurements.forEach((measurement) => {
      let measurementStats = size[measurement];

      if (includeEmptyMeasurements || hasMeasurementValue(measurementStats)) {
        let newBody = {
          size: size?.name,
          min: parseMeasurementValue(measurementStats?.min),
          max: parseMeasurementValue(measurementStats?.max),
        };
        if (size?.id) {
          newBody.id = size.id;
        }
        newData[measurement].push(newBody);
      }
    });
  });

  finalData = Object.keys(newData).map((row) => {
    return { title: row, measurements: newData[row] };
  });

  return finalData;
}

function parseMeasurementValue(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasEnabledMeasurement(stats) {
  return Boolean(stats && typeof stats === "object");
}

function getEnabledMeasurementsList(sizes) {
  if (!sizes || sizes.length < 1) return [];
  const sampleItem = sizes[0];
  return Object.keys(sampleItem).filter((m) => {
    if (m === "name" || m === "id") return false;
    return sizes.some((size) => hasEnabledMeasurement(size?.[m]));
  });
}

function hasMeasurementValue(stats) {
  if (!stats || typeof stats !== "object") return false;
  const min = parseMeasurementValue(stats.min);
  const max = parseMeasurementValue(stats.max);
  if (min === null || max === null) return false;
  // Historical payloads persisted empty cells as 0-0; treat those as empty.
  return !(min === 0 && max === 0);
}

export function formatClothingMeasurement({
  val,
  current,
  addUnits,
  originalMeasurement,
}) {
  if (!val) return "";
  let conversion = 0.393701; // 1 cm = this many inches
  if (current === TABLE_MEASUREMENTS.CM) {
    let newVal = val.toFixed(1);
    if (originalMeasurement === TABLE_MEASUREMENTS.INCHES)
      newVal = (val / conversion).toFixed(1);
    if (!addUnits) return newVal;

    return newVal + "cm";
  } else {
    let newVal = val.toFixed(1);
    if (originalMeasurement === TABLE_MEASUREMENTS.CM)
      newVal = (val * conversion).toFixed(1);
    if (!addUnits) return newVal;
    return newVal + '"';
  }
}

export function formatNumber(num) {
  if (num === null || num === undefined) return "";
  return num.toLocaleString("en-US");
}

/** Trim, strip angle brackets for simple XSS mitigation in plain-text fields. */
export function sanitizePlainText(value, maxLength = 200) {
  if (value == null) return "";
  let s = String(value).trimStart(); // Adjusting to trim start to account for users who want to add a space in the middle of their input. Currently this counters that.
  s = s.replace(/[<>]/g, "");
  if (maxLength && s.length > maxLength) s = s.slice(0, maxLength);
  return s;
}

/** Sizing chart measurement cell: allow empty while editing; when set, 1–4 digits. */
export function isValidSizingMeasurementDigits(val) {
  if (val === "" || val === null || val === undefined) return false;
  const s = String(val).trim();
  return /^\d{1,4}$/.test(s);
}

/** Min/max from API formatting or user input: non‑negative, max 9999 (4-digit cap). */
export function isValidSizingMeasurementValue(val) {
  if (val === "" || val === null || val === undefined) return false;
  const n = parseFloat(String(val).trim());
  if (!Number.isFinite(n) || n < 0) return false;
  return n <= 9999;
}

/**
 * Display name for a chart row: optional vendor override, else template label.
 */
export function getChartDisplayName(chartId, chartMeta) {
  const raw = chartMeta?.[chartId]?.displayName;
  const custom = sanitizePlainText(raw, 120);
  if (custom) return custom;
  return getChartLabel(chartId) || "—";
}

export const parseDate = (date, hasTime = true) => {
  if (!date) return;
  const d = new Date(date);
  if (d instanceof Date == false || isNaN(d)) return;

  if (hasTime) {
    return (
      d.toLocaleDateString() +
      `\n ${d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    );
  }

  return d.toLocaleDateString();
};
